/**
 * StripeConnectBanner
 *
 * Shows a soft amber banner when the company's Stripe account is not active.
 * Dismissible with a 24-hour localStorage TTL so it doesn't nag constantly.
 * Used on Dashboard and Invoices.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { CreditCard, X, ArrowRight } from 'lucide-react';

const DISMISS_KEY = 'kro_stripe_banner_dismissed_until';
const TTL_MS      = 24 * 60 * 60 * 1000; // 24 hours

export default function StripeConnectBanner({ companyId }) {
  const navigate  = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!companyId) return;

    // Respect dismiss TTL
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() < until) return;

    // Check company doc once
    getDoc(doc(db, 'companies', companyId))
      .then(snap => {
        const status = (snap.data() || {}).stripeConnectStatus;
        if (!status || status !== 'active') setVisible(true);
      })
      .catch(() => {});
  }, [companyId]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, (Date.now() + TTL_MS).toString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="stripe-connect-banner"
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
    >
      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <CreditCard className="w-4 h-4 text-amber-600" />
      </div>

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-amber-800">
          Connect Stripe to accept payments from clients.
        </span>
        <span className="text-xs text-amber-600 ml-2">
          Quotes work, but clients can't pay online until connected.
        </span>
      </div>

      <button
        onClick={() => navigate('/settings/integrations')}
        data-testid="stripe-banner-connect-btn"
        className="flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
      >
        Connect Now <ArrowRight className="w-3 h-3" />
      </button>

      <button
        onClick={dismiss}
        data-testid="stripe-banner-dismiss-btn"
        className="p-1 text-amber-400 hover:text-amber-600 transition-colors flex-shrink-0"
        title="Dismiss for 24 hours"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
